import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader, useExport } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, MultiSelect, Select } from "@dl/web-core-js/ui";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FileSpreadsheet } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PartClient, totalQuantity, type PartDTO } from "../../api/part";
import { MONTH_NAMES, type MonthName, isoDate } from "../../api/reporting";

type InventoryExport = "accounting" | "part" | "product" | "service" | "wip";

const monthLabel = (month: MonthName) =>
  new Date(2000, MONTH_NAMES.indexOf(month), 1).toLocaleString(undefined, { month: "long" });

/**
 * Parts: the active SIGM part list with group/qty filters, the inventory and
 * accounting exports, and a per-part detail (quantities, suppliers, purchase
 * orders, transactions) on its own route. Read-only, as in Vaadin.
 */
export function PartsPage() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const { start: startExport, busy } = useExport();
  const navigate = useNavigate();

  const parts = useMemo(() => new PartClient(http), [http]);

  const [exportKind, setExportKind] = useState<InventoryExport | null>(null);
  const today = isoDate(new Date());
  const [dateTo, setDateTo] = useState(today);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState<MonthName>(MONTH_NAMES[new Date().getMonth()]!);

  const { data, isLoading } = useQuery({
    queryKey: ["part", "extended-active"],
    queryFn: () => parts.extendedActive(),
    staleTime: 5 * 60_000,
  });

  const groupsQuery = useQuery({
    queryKey: ["part", "groups"],
    queryFn: () => parts.groups(),
    staleTime: 30 * 60_000,
    enabled: exportKind === "part",
  });

  const cols = useMemo(() => {
    const c = defineColumns<PartDTO>();
    return [
      c.text("no", (row) => row.no ?? "", { headerKey: "column.part.no", priority: 1 }),
      c.text("description", (row) => row.description1 ?? "", {
        headerKey: "column.part.description",
        priority: 2,
      }),
      c.custom(
        "group",
        (row) => row.part_group?.no ?? "",
        (row) => (
          <span>
            {row.part_group?.no}
            <span className="ml-1 text-[11px] text-ink-faint">{row.part_group?.description}</span>
          </span>
        ),
        { headerKey: "column.part.group", filter: "select", priority: 3 },
      ),
      c.custom(
        "active",
        (row) => (row.active ? "ACTIVE" : "INACTIVE"),
        (row) => (
          <Badge variant={row.active ? "success" : "error"} size="sm">
            {row.active ? "ACTIVE" : "INACTIVE"}
          </Badge>
        ),
        { headerKey: "column.part.active", filter: "select", defaultVisible: false },
      ),
      c.text("unit", (row) => (row.unit ?? "").toUpperCase(), { headerKey: "column.part.unit" }),
      c.number("real_qty", (row) => totalQuantity(row), {
        headerKey: "column.part.real-qty",
        priority: 3,
      }),
    ];
  }, []);

  const runExport = () => {
    const kind = exportKind;
    if (!kind) return;
    setExportKind(null);
    const action = {
      accounting: () => parts.documentAccounting(Number(year), month),
      part: () => parts.documentPartInventory(dateTo, groupIds.map(Number)),
      product: () => parts.documentProductInventory(dateTo),
      service: () => parts.documentServicePartInventory(dateTo),
      wip: () => parts.documentWipInventory(dateTo),
    }[kind];
    void startExport(`part-export-${kind}`, action, t("job.download.failed", "Download failed"));
  };

  const exportLabels: Record<InventoryExport, string> = {
    accounting: t("menu.item.export.accounting", "Accounting report"),
    part: t("menu.item.export.part-inventory", "Part inventory"),
    product: t("menu.item.export.product-inventory", "Product inventory"),
    service: t("menu.item.export.service-part-inventory", "Service parts"),
    wip: t("menu.item.export.wip-inventory", "WIP inventory"),
  };

  const years = Array.from({ length: 3 }, (_, index) => new Date().getFullYear() - index);

  return (
    <>
      <PageHeader
        title={t("navigation.item.parts", "Parts")}
        subtitle={t("parts.subtitle", "Active SIGM parts.")}
      />

      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/parts"
        loading={isLoading}
        onRowClick={(row) => void navigate({ to: "/parts/$partId", params: { partId: String(row.id) } })}
        actions={(Object.keys(exportLabels) as InventoryExport[]).map((kind) => ({
          label: exportLabels[kind],
          icon: <FileSpreadsheet className="size-4" />,
          disabled: busy !== null,
          onClick: () => setExportKind(kind),
        }))}
        emptyMessage={t("table.empty", "Nothing to show")}
      />

      <Dialog
        open={exportKind !== null}
        onClose={() => setExportKind(null)}
        title={
          exportKind === "accounting"
            ? t("dialog.export-accounting.title", "Export accounting report")
            : t("dialog.export-inventory.title", "Export inventory")
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => setExportKind(null)}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button onClick={runExport} disabled={exportKind === "part" && groupIds.length === 0}>
              {t("dialog.confirm.export", "Export")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {exportKind === "accounting" ? (
            <div className="flex gap-2">
              <Field label={t("form.field.year", "Year")} htmlFor="part-export-year">
                <Select
                  id="part-export-year"
                  value={year}
                  options={years.map((value) => ({ value: String(value), label: String(value) }))}
                  onChange={(event) => setYear(event.target.value)}
                  className="h-8 w-28"
                />
              </Field>
              <Field label={t("form.field.month", "Month")} htmlFor="part-export-month">
                <Select
                  id="part-export-month"
                  value={month}
                  options={MONTH_NAMES.map((value) => ({ value, label: monthLabel(value) }))}
                  onChange={(event) => setMonth(event.target.value as MonthName)}
                  className="h-8 w-40"
                />
              </Field>
            </div>
          ) : (
            <Field label={t("form.field.date-to", "Date to")} htmlFor="part-export-date">
              <Input
                id="part-export-date"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                className="h-8 w-44"
              />
            </Field>
          )}

          {exportKind === "part" && (
            <Field label={t("form.field.part-groups", "Part groups")} htmlFor="part-export-groups">
              <MultiSelect
                id="part-export-groups"
                options={(groupsQuery.data ?? []).map((group) => ({
                  value: String(group.id),
                  label: `${group.no ?? ""} - ${group.description ?? ""}`,
                }))}
                value={groupIds}
                onChange={setGroupIds}
                placeholder={t("pricing.select-clients", "Select")}
              />
            </Field>
          )}
        </div>
      </Dialog>
    </>
  );
}
